import React from 'react';
import { CheckCircle, ExternalLink, Award, Shield, Cpu, Wifi, Brain, Server, Camera, Building2, Satellite, Radio } from 'lucide-react';
import { hardware, technicalSpecs } from '../data/mock';
import { Button } from '../components/ui/button';

const Hardware = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1594915440248-1e419eba6611"
            alt="Network Equipment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-cyan-950/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium">
              Carrier-Grade Equipment
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Hardware <span className="text-cyan-400">Showcase</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
            Enterprise-grade equipment from Ubiquiti, MikroTik, Juniper, Netgate, and Mimosa for reliable carrier-class networks
          </p>
          <div className="inline-block px-6 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-xl">
            <p className="text-cyan-400 font-semibold">
              🏆 Trusted by 150+ ISP Deployments | 99.9% Network Uptime | 45,000+ Subscribers
            </p>
          </div>
        </div>
      </section>

      {/* Hardware Showcase */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {hardware.map((item, index) => (
              <div 
                key={item.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-block px-4 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-xs font-medium mb-4">
                    {item.category}
                  </div>
                  <h2 className="text-4xl font-bold text-white mb-2">{item.brand}</h2>
                  <h3 className="text-2xl text-cyan-400 mb-6">{item.model}</h3>
                  <p className="text-gray-400 leading-relaxed mb-8">
                    {item.description}
                  </p>

                  <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 mb-6">
                    <h4 className="text-white font-semibold mb-4">Key Specifications</h4>
                    <ul className="space-y-3">
                      {item.specs.map((spec, specIndex) => (
                        <li key={specIndex} className="flex items-start text-gray-300">
                          <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                          <span className="text-sm">{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center space-x-3 mb-6">
                    <span className="text-gray-400 text-sm font-medium">Ideal for:</span>
                    {item.useCases.map((useCase, ucIndex) => (
                      <span 
                        key={ucIndex}
                        className="px-3 py-1 bg-slate-800 border border-slate-700 rounded-lg text-cyan-400 text-xs"
                      >
                        {useCase}
                      </span>
                    ))}
                  </div>

                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-xl transition-all duration-200">
                    Learn More
                    <ExternalLink className="ml-2" size={16} />
                  </Button>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="bg-slate-900 border border-slate-700 rounded-2xl p-12 flex items-center justify-center">
                    <img 
                      src={`https://images.unsplash.com/photo-${
                        item.id === 1 ? '1558494949-ef010cbdcc31' : 
                        item.id === 2 ? '1639066648921-82d4500abf1a' : 
                        '1594915440248-1e419eba6611'
                      }`}
                      alt={`${item.brand} ${item.model}`}
                      className="rounded-xl w-full h-80 object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Comparison */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Deployment Recommendations
            </h2>
            <p className="text-xl text-gray-400">
              Choosing the right equipment for your scale
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden">
              <thead>
                <tr className="bg-slate-800">
                  <th className="px-6 py-4 text-left text-white font-semibold">Deployment Size</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Recommended Vendor</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Key Features</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Price Range</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-700">
                  <td className="px-6 py-4 text-white font-medium">Micro POP (120-250 clients)</td>
                  <td className="px-6 py-4 text-cyan-400">Ubiquiti EdgeRouter</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">UISP integration, built-in NMS & CRM, low power</td>
                  <td className="px-6 py-4 text-gray-400">₱80,000 - ₱150,000</td>
                </tr>
                <tr className="border-t border-slate-700 bg-slate-900/30">
                  <td className="px-6 py-4 text-white font-medium">Community Core (500 clients)</td>
                  <td className="px-6 py-4 text-cyan-400">Ubiquiti EdgeRouter / UISP Router</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">UISP ecosystem, scalable ISP management</td>
                  <td className="px-6 py-4 text-gray-400">₱150,000 - ₱400,000</td>
                </tr>
                <tr className="border-t border-slate-700">
                  <td className="px-6 py-4 text-white font-medium">Regional Core (4000+ clients)</td>
                  <td className="px-6 py-4 text-cyan-400">Ubiquiti EdgeRouter / UISP Router (High-End)</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Enterprise UISP, high throughput, full ISP stack</td>
                  <td className="px-6 py-4 text-gray-400">₱400,000 - ₱1,500,000</td>
                </tr>
                <tr className="border-t border-slate-700 bg-slate-900/30">
                  <td className="px-6 py-4 text-white font-medium">Large-Scale Network (10,000+ clients)</td>
                  <td className="px-6 py-4 text-cyan-400">Juniper Core + EdgeRouter Distribution</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Carrier-grade core, EdgeRouters as distribution layer</td>
                  <td className="px-6 py-4 text-gray-400">₱2,000,000 - ₱5,000,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* UISP Note */}
          <div className="mt-8 bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-cyan-400" size={20} />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Why We Recommend Ubiquiti for ISP Operations</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  The Ubiquiti UISP ecosystem provides a complete ISP management solution with <span className="text-cyan-400 font-medium">built-in NMS (Network Management System)</span> and <span className="text-cyan-400 font-medium">CRM integration</span>. This makes managing ISP operations smoother and easier compared to piecing together separate tools. For networks that grow beyond 10,000+ clients, we transition to <span className="text-cyan-400 font-medium">Juniper as the core</span> with EdgeRouters serving as distribution routers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why These Vendors */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why We Choose These Vendors
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Ubiquiti</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Cost-effective for Micro POPs with easy management and low power draw.
              </p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>• Simple web UI</li>
                <li>• Low power (~20-30W)</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">MikroTik</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Best price-to-performance for Community Cores with full routing support.
              </p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>• OSPF/BGP ready</li>
                <li>• Highly customizable</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Juniper</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                True carrier-grade platform for Regional Cores with enterprise support.
              </p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>• Global reliability</li>
                <li>• Advanced security</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Netgate/pfSense</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Enterprise-grade firewall and VPN solutions with open-source flexibility.
              </p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>• Advanced firewall</li>
                <li>• VPN concentrator</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Mimosa</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                High-performance wireless backhauls for long-distance PtP/PtMP links.
              </p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>• Spectrum reuse</li>
                <li>• GPS sync</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Cambium</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Reliable wireless solutions for rural and underserved area deployments.
              </p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>• ePMP series</li>
                <li>• cnPilot APs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Excellence - Certifications & Capabilities */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium">
                Technical Excellence
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Professional Certifications & Capabilities
            </h2>
            <p className="text-xl text-gray-400">
              Our team holds industry-recognized certifications across networking, wireless, and AI
            </p>
          </div>

          {/* First Row - Core Certifications */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Networking Certifications */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-6">
                <Server className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Networking</h3>
              <ul className="space-y-3">
                {technicalSpecs.certifications.networking.map((cert, index) => (
                  <li key={index} className="flex items-start text-gray-300 text-sm">
                    <Award className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* MikroTik Stack */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mb-6">
                <Cpu className="text-orange-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">MikroTik Stack</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {technicalSpecs.certifications.mikrotik.map((cert, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-400 text-xs font-semibold"
                  >
                    {cert}
                  </span>
                ))}
              </div>
              <p className="text-gray-400 text-sm">
                Full MikroTik certification stack: Network Associate, Routing Engineer, Traffic Control, and Wireless Engineer.
              </p>
            </div>

            {/* Security & Automation */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-red-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-6">
                <Camera className="text-red-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Security & Automation</h3>
              <ul className="space-y-3">
                {technicalSpecs.certifications.security.map((cert, index) => (
                  <li key={index} className="flex items-start text-gray-300 text-sm">
                    <Award className="text-red-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AI & Digital Innovation */}
            <div className="bg-gradient-to-br from-slate-800/50 to-purple-900/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mb-6">
                <Brain className="text-purple-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">AI & Digital Innovation</h3>
              <ul className="space-y-3 mb-4">
                {technicalSpecs.certifications.ai.map((cert, index) => (
                  <li key={index} className="flex items-start text-gray-300 text-sm">
                    <Award className="text-purple-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-purple-500/20">
                <p className="text-purple-300 text-sm font-medium mb-2">Deployed:</p>
                <p className="text-gray-400 text-sm">
                  AI-driven Customer Service Representatives (CSRs) for internal and enterprise client operations.
                </p>
              </div>
            </div>
          </div>

          {/* Second Row - Ecosystem & Specialty */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Full-Stack UniFi Ecosystem */}
            <div className="bg-gradient-to-br from-slate-800/50 to-cyan-900/20 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <Building2 className="text-cyan-400" size={28} />
                </div>
                <span className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-cyan-400 text-xs font-semibold">
                  Full Stack
                </span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">UniFi Ecosystem</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-cyan-400 text-xs font-semibold mb-2">NETWORKING</p>
                  <div className="flex flex-wrap gap-1">
                    {technicalSpecs.unifiEcosystem.networking.map((item, index) => (
                      <span key={index} className="px-2 py-1 bg-slate-700/50 rounded text-gray-300 text-xs">{item}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-cyan-400 text-xs font-semibold mb-2">UniFi OS APPLICATIONS</p>
                  <div className="flex flex-wrap gap-1">
                    {technicalSpecs.unifiEcosystem.applications.map((item, index) => (
                      <span key={index} className="px-2 py-1 bg-slate-700/50 rounded text-gray-300 text-xs">{item}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-cyan-400 text-xs font-semibold mb-2">NETWORK DESIGN</p>
                  <div className="flex flex-wrap gap-1">
                    {technicalSpecs.unifiEcosystem.design.map((item, index) => (
                      <span key={index} className="px-2 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-300 text-xs">{item}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Wireless & Backhaul */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-green-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-green-500/10 rounded-xl flex items-center justify-center mb-6">
                <Radio className="text-green-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Wireless & Backhaul</h3>
              <ul className="space-y-3 mb-4">
                {technicalSpecs.certifications.wireless.map((cert, index) => (
                  <li key={index} className="flex items-start text-gray-300 text-sm">
                    <Award className="text-green-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-400 text-sm mb-4">
                Specialized in long-range PtP/PtMP wireless deployments for hazard-averse backhaul solutions.
              </p>
              <div className="pt-4 border-t border-slate-600">
                <p className="text-green-400 text-xs font-semibold mb-2">EXPERTISE</p>
                <ul className="text-gray-400 text-xs space-y-1">
                  <li>• Mimosa B5x/C5x deployments</li>
                  <li>• Cambium ePMP & cnPilot</li>
                  <li>• LOS/NLOS planning</li>
                </ul>
              </div>
            </div>

            {/* Satellite Connectivity - Starlink */}
            <div className="bg-gradient-to-br from-slate-800/50 to-blue-900/30 backdrop-blur-sm border border-blue-500/30 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-blue-500/20 border border-blue-500/40 rounded-full text-blue-300 text-xs font-bold animate-pulse">
                  {technicalSpecs.starlink.deployments}+ Deployed
                </span>
              </div>
              <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6">
                <Satellite className="text-blue-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Satellite Connectivity</h3>
              <p className="text-blue-400 text-sm font-semibold mb-4">Starlink Pioneers</p>
              
              <p className="text-gray-400 text-sm mb-4">
                {technicalSpecs.starlink.description}
              </p>
              
              <div className="space-y-2">
                {technicalSpecs.starlink.features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="flex items-start text-gray-300 text-sm">
                    <CheckCircle className="text-blue-400 mr-2 mt-0.5 flex-shrink-0" size={14} />
                    <span className="text-xs">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-white text-center mb-8">Core Capabilities</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {technicalSpecs.capabilities.map((capability, index) => (
                <div 
                  key={index}
                  className="bg-slate-800/30 border border-slate-700 rounded-xl p-4 text-center hover:border-cyan-500/50 transition-all"
                >
                  <CheckCircle className="text-cyan-400 mx-auto mb-2" size={20} />
                  <span className="text-gray-300 text-sm">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Gallery */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Installation Gallery
            </h2>
            <p className="text-xl text-gray-400">
              Professional deployments across the Philippines
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative group overflow-hidden rounded-2xl">
              <img 
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31"
                alt="Installation 1"
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-6">
                <span className="text-white font-semibold">Server Room Installation</span>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl">
              <img 
                src="https://images.unsplash.com/photo-1639066648921-82d4500abf1a"
                alt="Installation 2"
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-6">
                <span className="text-white font-semibold">Network Core Setup</span>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl">
              <img 
                src="https://images.unsplash.com/photo-1594915440248-1e419eba6611"
                alt="Installation 3"
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-6">
                <span className="text-white font-semibold">Carrier Equipment Rack</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-950 via-cyan-950/30 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Need Equipment Consultation?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Our technical team can help you select the perfect hardware for your deployment.
          </p>
          <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-200">
            Contact Technical Sales
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Hardware;
